/**
 * Orchestrator - UI-side auto-delegation
 *
 * Analyzes tasks and determines which band member should handle them.
 * No suggestions - just routes automatically.
 */

export interface BandMember {
  id: string;
  name: string;
  icon: string;
  color: string;
}

export interface DelegationPlan {
  primary: BandMember;
  supporting: BandMember[];
  parallel: boolean;
  reasoning: string;
  ephemeralWorkers: number;
}

// Band member definitions (simplified for UI)
export const BAND_MEMBERS: Record<string, BandMember> = {
  orchestrator: { id: "orchestrator", name: "Orchestrator", icon: "🎯", color: "#FFD000" },
  researcher: { id: "researcher", name: "Researcher", icon: "🔍", color: "#4A90D9" },
  coder: { id: "coder", name: "Coder", icon: "💻", color: "#50C878" },
  analyst: { id: "analyst", name: "Analyst", icon: "📊", color: "#9B59B6" },
  communicator: { id: "communicator", name: "Communicator", icon: "✍️", color: "#E74C3C" },
  support: { id: "support", name: "Support", icon: "🤝", color: "#1ABC9C" },
  legal: { id: "legal", name: "Legal", icon: "⚖️", color: "#34495E" },
  security: { id: "security", name: "Security", icon: "🔒", color: "#2C3E50" },
  sales: { id: "sales", name: "Sales", icon: "💰", color: "#27AE60" },
  finance: { id: "finance", name: "Finance", icon: "📈", color: "#F39C12" },
  people: { id: "people", name: "People", icon: "👥", color: "#E91E63" },
  pm: { id: "pm", name: "PM", icon: "📋", color: "#3498DB" },
  bi: { id: "bi", name: "BI", icon: "🎯", color: "#8E44AD" },
};

// Keywords that map to band members
const ROUTING_KEYWORDS: Record<string, string[]> = {
  researcher: [
    "research", "investigate", "find out", "look into", "due diligence",
    "competitive", "market", "competitors", "industry", "trends",
  ],
  coder: [
    "code", "build", "implement", "develop", "script", "automate",
    "integrate", "api", "debug", "fix bug", "deploy", "pipeline",
  ],
  analyst: [
    "analyze data", "metrics", "kpi", "forecast", "model", "statistics",
    "dashboard", "report", "visualization", "chart",
  ],
  communicator: [
    "write", "draft", "email", "message", "presentation", "deck",
    "investor update", "board", "press release", "announce",
  ],
  support: [
    "help", "support", "troubleshoot", "customer", "faq", "documentation",
  ],
  legal: [
    "contract", "legal", "compliance", "term sheet", "agreement", "policy", "nda",
  ],
  security: [
    "security", "audit", "soc2", "vulnerability", "penetration", "gdpr", "hipaa",
  ],
  sales: [
    "sales", "outreach", "prospect", "pipeline", "proposal", "pitch", "deal", "lead",
  ],
  finance: [
    "financial", "budget", "forecast", "expense", "revenue", "p&l", "cash flow",
  ],
  people: [
    "hire", "recruit", "job description", "onboarding", "culture", "employee", "hr",
  ],
  pm: [
    "project", "timeline", "milestone", "standup", "sprint", "deliverable", "deadline",
  ],
  bi: [
    "business intelligence", "portfolio", "benchmark", "roll-up", "cross-company", "thesis",
  ],
};

/**
 * Analyze a message and create a delegation plan
 */
export function createDelegationPlan(message: string): DelegationPlan {
  const lower = message.toLowerCase();

  // Find matching band members
  const matches: Array<{ id: string; score: number }> = [];

  for (const [memberId, keywords] of Object.entries(ROUTING_KEYWORDS)) {
    let score = 0;
    for (const keyword of keywords) {
      if (lower.includes(keyword)) {
        score += keyword.split(" ").length; // Multi-word = higher score
      }
    }
    if (score > 0) {
      matches.push({ id: memberId, score });
    }
  }

  // Sort by score
  matches.sort((a, b) => b.score - a.score);

  // Determine if parallel work is needed
  const hasParallelIndicators = [
    "simultaneously", "in parallel", "while also", "both", "all of", "multiple",
  ].some((kw) => lower.includes(kw));

  const hasMultipleSteps = (message.match(/^\s*\d+[.)]\s/gm) || []).length >= 2;

  // Pick primary and supporting members
  const primaryId = matches.length > 0 ? matches[0].id : "researcher";
  const supportingIds = matches.slice(1, 3).map((m) => m.id);

  const primary = BAND_MEMBERS[primaryId];
  const supporting = supportingIds.map((id) => BAND_MEMBERS[id]);

  // Calculate ephemeral workers
  const parallel = hasParallelIndicators || (matches.length >= 2 && hasMultipleSteps);
  const ephemeralWorkers = parallel ? Math.min(matches.length, 3) : 0;

  // Generate reasoning
  let reasoning = "";
  if (supporting.length === 0) {
    reasoning = `Routing to ${primary.name}`;
  } else if (parallel) {
    const names = [primary.name, ...supporting.map((s) => s.name)].join(", ");
    reasoning = `Parallel: ${names}`;
  } else {
    reasoning = `${primary.name} (lead)` + (supporting.length > 0 ? ` + ${supporting.map((s) => s.name).join(", ")}` : "");
  }

  return {
    primary,
    supporting,
    parallel,
    reasoning,
    ephemeralWorkers,
  };
}

/**
 * Get a simple delegation indicator string
 */
export function getDelegationIndicator(plan: DelegationPlan): string {
  if (plan.supporting.length === 0) {
    return `${plan.primary.icon} ${plan.primary.name}`;
  }
  const icons = [plan.primary.icon, ...plan.supporting.map((s) => s.icon)].join("");
  return `${icons} ${plan.reasoning}`;
}

/**
 * Check if task needs orchestration (vs simple single-agent)
 */
export function needsOrchestration(message: string): boolean {
  const plan = createDelegationPlan(message);
  return plan.supporting.length > 0 || plan.ephemeralWorkers > 0;
}
